// Modules:
import React, { useState, useContext } from 'react';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Style:
import AppBar from '@mui/material/AppBar';
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    useGridApiRef,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import PlayForWorkIcon from '@mui/icons-material/PlayForWork';

// Styles
import muiTheme from 'theme';

// Config
import config from 'config/config';
import { formatRows, determineSortModel } from 'utilities/utils';
import { exportAsCsv } from '../../utilities/ncdmUtils';

// Components:
import CustomDataGridProFooter from '../UI/CustomDataGridProFooter';

// Styles:
const styles = {
    '&.MuiAccordionDetails-root': {
        flexDirection: 'column',
        padding: 0,
    },
    '&.MuiDataGrid-root': {
        fontSize: '0.8rem',
    },
    '&.MuiAppBar-root': {
        backgroundColor: muiTheme.palette.darkGray.main,
    },
    '&.MuiTab-root': {
        maxWidth: '350px',
        padding: '0.688rem',
    },
    '&.Mui-selected': {
        color: 'white',
    },
    '&.MuiTypography-root': {
        fontSize: '1em',
        color: muiTheme.palette.text.secondary,
        marginRight: '0.625rem',
    },
    '&.MuiButton-root': {
        color: 'black',
    },
};

// Add accessibility content
function a11yProps(index) {
    return {
        id: `table-tab-${index}`,
        'aria-controls': `table-tabpanel-${index}`,
    };
}

const CustomToolbar = ({
    apiRef,
    selection,
    columnHeaders,
    selectedLayerSet,
}) => {
    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // Methods:
    const handleClick = () => {
        const uiCsv = apiRef.current.getDataAsCsv();

        // Building string for year data. Only applicable to maps (charts are trends over time)
        let yearDetails;
        if (currentTab.contentType === 'map') {
            const layersWithYearData = selectedLayerSet.setLayers.filter(
                (layer) => layer.layerYear
            );
            yearDetails = layersWithYearData
                .map((layer) => `${layer.title} (${layer.layerYear})`)
                .join(' and ');
        }

        const title = `${currentTab.exportTitle}${
            selection ? ` - ${selection}` : ''
        }`;

        exportAsCsv({
            uiCsv,
            title,
            subtitle: currentTab.exportSubtitle,
            yearDetails,
            columnHeaders,
        });
    };

    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton sx={styles} />
            <GridToolbarFilterButton sx={styles} />
            <Button onClick={() => handleClick()} className="export-button">
                <PlayForWorkIcon />
                EXPORT
            </Button>
        </GridToolbarContainer>
    );
};

// Set the tabs panel XGrid contents
function TabPanel({
    counties,
    tabNumber,
    layer,
    index,
    loading,
    mapRef,
    tableData,
    formatColumns,
    selectedLayerSet,
    setMissingSuppressedModalOpen,
    stratification,
}) {
    // State
    const [highlightedPolygon, setHighlightedPolygon] = useState(null);

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // Hooks:
    const apiRef = useGridApiRef();

    const handleClick = (e) => {
        // Use the layer info to determine what type of geography is displayed and what field name we need to filter with
        const geography =
            tableData[0].info.id.split('-')[0] === 'county'
                ? 'County'
                : 'TractName';

        // Clear highlighting if there is a previously selection layer and reset to original styling
        if (highlightedPolygon) {
            const { color, weight, opacity } = highlightedPolygon;
            highlightedPolygon.selection.setStyle({ color, weight, opacity });
        }

        // Find selection from map that matches the clicked row
        // eslint-disable-next-line no-underscore-dangle
        const layers = Object.values(mapRef.current._layers);

        if (layers) {
            const [selection] = layers
                .filter((allLayers) => allLayers.feature)
                .filter(
                    (featureLayer) =>
                        featureLayer.feature.geometry.type !== 'Point'
                )
                .filter(
                    geography === 'County'
                        ? (polygonFeature) =>
                              polygonFeature.feature.properties[geography] ===
                              e.row[geography]
                        : (polygonFeature) =>
                              polygonFeature.feature.properties.TractCode ===
                              e.row.TractCode
                );

            if (selection) {
                // Copy the selection into state for later reference when reverting styles
                const { color, weight, opacity } = selection.options;
                // Set state
                setHighlightedPolygon({ color, weight, opacity, selection });

                // Check if the current selection matches the previous selection
                if (
                    highlightedPolygon &&
                    highlightedPolygon.selection.feature.properties[
                        geography
                    ] === selection.feature.properties[geography]
                ) {
                    // Set state. If here we are deselecting the area
                    setHighlightedPolygon(null);
                } else {
                    // If here we are highlighting a new selection
                    selection.setStyle({
                        color: muiTheme.palette.mapHighlight.main,
                        weight: 5,
                    });
                }
            }
        }
    };

    const determineSelection = () => {
        const { selection } = tableData[0];
        if (!selection) {
            return null;
        }

        let selectionName;
        if (counties.length) {
            // This ternary is to account for selections that relate to years instead of counties.
            // This supplies the selection to the .csv export title:
            selectionName = counties.find((county) => county.code === selection)
                ? counties.find((county) => county.code === selection).county
                : selection;
        }
        return selectionName;
    };

    return (
        tabNumber === index && (
            <div style={{ height: 600, width: '100%' }}>
                <DataGridPro
                    initialState={{
                        sorting: {
                            sortModel: determineSortModel(currentTab),
                        },
                    }}
                    apiRef={apiRef}
                    key={layer.id}
                    disableColumnMenu
                    disableSelectionOnClick
                    columns={formatColumns(layer.info.columnHeaders)}
                    rows={formatRows(layer, stratification, currentTab)}
                    pageSize={config.tablePageSize}
                    loading={loading}
                    sx={styles}
                    rowHeight={config.tableRowHeight}
                    onRowClick={mapRef?.current ? (e) => handleClick(e) : null}
                    components={{
                        Toolbar: CustomToolbar,
                        Footer: CustomDataGridProFooter,
                    }}
                    componentsProps={{
                        footer: { setMissingSuppressedModalOpen },
                        toolbar: {
                            apiRef,
                            currentTab,
                            columnHeaders: layer.info.columnHeaders,
                            selection: determineSelection(),
                            selectedLayerSet,
                        },
                    }}
                    // Note: the following forces the "tabIndex" property of the cells to remain "-1" which disables the focus event from occuring. An undesired focus caused the page to jump from map to table when entries were filtered from the table by extent. Leaving the "tabIndex" attribute on each cell at -1 allows them to be keyboard selectable (complying with 508/ADA) but still non-focusable.
                    state={{
                        keyboard: {
                            cell: null,
                        },
                    }}
                />
            </div>
        )
    );
}

const AttributeTable = ({
    counties,
    tableData,
    isLoading,
    mapRef,
    formatColumns,
    selectedLayerSet,
    setMissingSuppressedModalOpen,
    stratification,
}) => {
    // // State // //
    const [tabNumber, setTabNumber] = useState(0);
    const [expanded, setExpanded] = useState('tableData');

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // // Methods // //
    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleTabChange = (event, newValue) => {
        setTabNumber(newValue);
    };

    // // Render // //
    return (
        <>
            {currentTab.contentType === 'table' ? (
                tableData.map((layer, index) => (
                    <TabPanel
                        key={layer.id}
                        counties={counties}
                        mapRef={mapRef}
                        muiTheme={muiTheme}
                        tabNumber={tabNumber}
                        layer={layer}
                        index={index}
                        loading={isLoading}
                        tableData={tableData}
                        currentTab={currentTab}
                        formatColumns={formatColumns}
                        selectedLayerSet={selectedLayerSet}
                        setMissingSuppressedModalOpen={
                            setMissingSuppressedModalOpen
                        }
                        stratification={stratification}
                    />
                ))
            ) : (
                <Accordion
                    expanded={expanded === 'tableData'}
                    onChange={handleChange('tableData')}
                >
                    <div className="accordion-summary-container">
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="tableData-content"
                            id="tableData-header"
                        >
                            <Typography sx={styles}>
                                {expanded ? 'Show less' : 'Show more'}
                            </Typography>
                        </AccordionSummary>
                    </div>
                    <AccordionDetails sx={styles}>
                        <AppBar position="static" sx={styles}>
                            <Tabs
                                value={tabNumber}
                                indicatorColor="secondary"
                                onChange={handleTabChange}
                                aria-label="map layer table content"
                                TabIndicatorProps={{
                                    sx: {
                                        backgroundColor:
                                            'rgba(255,255,255, 0.8)',
                                        height: 3,
                                    },
                                }}
                            >
                                {tableData.map((layer, index) => (
                                    <Tab
                                        key={layer.id}
                                        label={layer.info.tableTitle}
                                        {...a11yProps(index)}
                                        sx={styles}
                                    />
                                ))}
                            </Tabs>
                        </AppBar>
                        {tableData.map((layer, index) => (
                            <TabPanel
                                key={layer.id}
                                counties={counties}
                                mapRef={mapRef}
                                muiTheme={muiTheme}
                                tabNumber={tabNumber}
                                layer={layer}
                                index={index}
                                loading={isLoading}
                                tableData={tableData}
                                currentTab={currentTab}
                                formatColumns={formatColumns}
                                selectedLayerSet={selectedLayerSet}
                                setMissingSuppressedModalOpen={
                                    setMissingSuppressedModalOpen
                                }
                                stratification={stratification}
                            />
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}
        </>
    );
};

export default AttributeTable;
